// Agency view: shows the current user's agency or an empty-state CTA. Any
// authenticated user may found one (becoming its FOUNDER); FOUNDER/ADMIN
// may invite members by email and edit.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { auth } from '../core/auth.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { showModal } from '../components/Modal.js';
import { toast } from '../components/Toast.js';
import { showFormError, fieldFromApiError } from '../core/formError.js';

export class AgencyView extends Component {
  constructor() {
    super();
    this.state = { agency: null, members: [], pending: [], loaded: false, error: null, lastInvite: null };
  }

  template() {
    if (!this.state.loaded) return `<section class="stack"><div class="muted">${esc(t('common.loading'))}</div></section>`;
    if (this.state.error)   return `<section class="stack"><div class="error-banner">${esc(this.state.error)}</div></section>`;
    if (!this.state.agency) return this._emptyHtml();
    return this._agencyHtml();
  }

  _emptyHtml() {
    // Any authenticated user may found an agency (becoming its FOUNDER).
    // The server rejects the attempt if they already belong to one.
    const canCreate = auth.isAuthenticated;
    return `
      <section class="stack">
        <h2>${esc(t('agency.title'))}</h2>
        <div class="card empty-card">
          <div class="empty-icon">${icon('briefcase', { size: 'xl' })}</div>
          <p>${esc(t('agency.empty_message'))}</p>
          ${canCreate
            ? `<button class="btn btn-primary" data-action="create">${esc(t('agency.create_cta'))}</button>`
            : `<p class="muted">${esc(t('agency.no_permission'))}</p>`}
        </div>
      </section>
    `;
  }

  _agencyHtml() {
    const a = this.state.agency;
    const role = a.my_role || 'MEMBER';
    const canManage = role === 'FOUNDER' || role === 'ADMIN';
    const members = this.state.members;
    const pending = this.state.pending;
    return `
      <section class="stack">
        <div class="row-between">
          <h2>${esc(t('agency.title'))}</h2>
          ${canManage ? `<button class="btn btn-ghost" data-action="edit">${icon('edit', { size: 'sm' })}<span>${esc(t('common.edit'))}</span></button>` : ''}
        </div>
        <div class="card stack">
          <div class="row-between">
            <strong>${esc(a.name)}</strong>
            <span class="status-badge status-${esc(role)}">${esc(role)}</span>
          </div>
          ${a.billing_email ? `<div class="muted">${esc(a.billing_email)}</div>` : ''}
          <div class="faint">${esc(a.member_count || 1)} ${esc(t('agency.members_count'))}</div>
        </div>
        ${this._lastInviteBannerHtml()}
        <div class="card stack">
          <div class="row-between">
            <h3>${esc(t('agency.members_title'))}</h3>
            ${canManage ? `<button class="btn btn-ghost" data-action="invite">${icon('plus', { size: 'sm' })}<span>${esc(t('agency.add_member'))}</span></button>` : ''}
          </div>
          ${members.length === 0
            ? `<div class="muted">${esc(t('agency.no_members'))}</div>`
            : `<div class="list">${members.map((m) => this._memberRow(m, canManage, role)).join('')}</div>`}
          ${pending.length > 0 ? `
            <h4 class="faint">${esc(t('agency.pending_invites'))}</h4>
            <div class="list">${pending.map((p) => this._pendingRow(p)).join('')}</div>
          ` : ''}
        </div>
      </section>
    `;
  }

  /**
   * One-shot banner with the share-link after inviting a not-yet-Nestly
   * email (no MTA is wired up — the inviter copies the link themselves).
   */
  _lastInviteBannerHtml() {
    const i = this.state.lastInvite;
    if (!i) return '';
    return `
      <div class="info-banner" data-last-invite>
        <div class="stack-2">
          <strong>${esc(t('agency.invite_sent_title'))}</strong>
          <span class="faint">${esc(t('agency.invite_sent_body', { email: i.email }))}</span>
        </div>
        <div class="row" style="gap: 8px; align-items: center; margin-top: 10px; flex-wrap: wrap;">
          <input type="text" class="input mono" readonly value="${esc(i.url)}" data-invite-url style="flex: 1; min-width: 240px; font-size: 12px;" />
          <button type="button" class="btn btn-sm" data-action="copy-invite-url">${esc(t('common.copy'))}</button>
          <button type="button" class="btn-mono" data-action="dismiss-last-invite">${esc(t('common.close'))}</button>
        </div>
      </div>
    `;
  }

  _pendingRow(p) {
    return `
      <div class="list-item">
        <div class="stack-2 grow">
          <strong>${esc(p.email || p.full_name || '—')}</strong>
          <span class="faint">${esc(t('members.pending'))}</span>
        </div>
        <span class="status-badge">${esc(p.role || '')}</span>
      </div>
    `;
  }

  _memberRow(m, canManage, myRole) {
    const isFounder = m.role === 'FOUNDER';
    const canRemove = canManage && !isFounder && (myRole === 'FOUNDER' || m.role !== 'ADMIN');
    return `
      <div class="list-item">
        <div class="stack-2 grow">
          <strong>${esc(m.user_full_name || m.user_email)}</strong>
          <span class="faint">${esc(m.user_email)}</span>
        </div>
        <span class="status-badge status-${esc(m.role)}">${esc(m.role)}</span>
        ${canRemove ? `<button class="btn btn-ghost btn-icon" data-remove="${esc(m.id)}" aria-label="${esc(t('common.remove'))}">${icon('x', { size: 'sm' })}</button>` : ''}
      </div>
    `;
  }

  mount() {
    if (!this.state.loaded) { this._load(); return; }
    this.el.querySelector('[data-action="create"]')?.addEventListener('click', () => this._openCreateModal());
    this.el.querySelector('[data-action="edit"]')?.addEventListener('click', () => this._openEditModal());
    this.el.querySelector('[data-action="invite"]')?.addEventListener('click', () => this._openInviteModal());
    this.el.querySelector('[data-action="copy-invite-url"]')?.addEventListener('click', () => this._copyInviteUrl());
    this.el.querySelector('[data-action="dismiss-last-invite"]')?.addEventListener('click', () => this.setState({ lastInvite: null }));
    this.el.querySelectorAll('[data-remove]').forEach((btn) => {
      btn.addEventListener('click', () => this._removeMember(btn.getAttribute('data-remove')));
    });
  }

  async _load() {
    try {
      const a = await api.get('/agencies/me').catch((e) => { if (e instanceof ApiError && e.status === 404) return null; throw e; });
      let members = [];
      let pending = [];
      if (a) {
        const res = await api.get(`/agencies/${a.id}/members`).catch(() => ({ data: [], pending: [] }));
        members = res.data || [];
        pending = Array.isArray(res.pending) ? res.pending : [];
      }
      this.setState({ agency: a, members, pending, loaded: true });
    } catch (err) {
      this.setState({ loaded: true, error: err instanceof ApiError ? err.message : t('common.failed_to_load') });
    }
  }

  _openCreateModal() {
    const body = `
      <div data-form-error></div>
      <form class="stack" data-form>
        <div class="field"><label>${esc(t('agency.name'))}</label><input class="input" name="name" required /></div>
        <div class="field"><label>${esc(t('agency.billing_email'))}</label><input class="input" name="billing_email" type="email" /></div>
      </form>`;
    const m = showModal({
      title: t('agency.create_cta'),
      body,
      actions: [
        { label: t('common.cancel') },
        { label: t('common.create'), variant: 'primary', onClick: async () => {
          const host = m.el.querySelector('[data-modal-body]');
          const data = Object.fromEntries(new FormData(host.querySelector('[data-form]')).entries());
          try {
            await api.post('/agencies', data);
            // Founding layers AGENCY_STAFF onto the account server-side —
            // refresh the session profile so role-derived UI (agency issuer
            // step in the property wizard, etc.) sees it immediately.
            try { const me = await api.get('/auth/me'); auth.patchUser({ roles: me.roles }); } catch {}
            toast.success(t('agency.created'));
            this.setState({ loaded: false });
            this._load();
          } catch (e) {
            const message = e instanceof ApiError ? e.message : t('agency.errors.create');
            const field = e instanceof ApiError ? fieldFromApiError(e, ['name', 'billing_email']) : null;
            showFormError(host, message, { field });
            return false;
          }
        }},
      ],
    });
  }

  _openEditModal() {
    const a = this.state.agency;
    const body = `
      <div data-form-error></div>
      <form class="stack" data-form>
        <div class="field"><label>${esc(t('agency.name'))}</label><input class="input" name="name" value="${esc(a.name)}" required /></div>
        <div class="field"><label>${esc(t('agency.billing_email'))}</label><input class="input" name="billing_email" type="email" value="${esc(a.billing_email || '')}" /></div>
      </form>`;
    const m = showModal({
      title: t('common.edit'),
      body,
      actions: [
        { label: t('common.cancel') },
        { label: t('common.save'), variant: 'primary', onClick: async () => {
          const host = m.el.querySelector('[data-modal-body]');
          const data = Object.fromEntries(new FormData(host.querySelector('[data-form]')).entries());
          try { await api.patch(`/agencies/${a.id}`, data); toast.success(t('common.save')); this.setState({ loaded: false }); this._load(); }
          catch (e) {
            const message = e instanceof ApiError ? e.message : t('common.error');
            const field = e instanceof ApiError ? fieldFromApiError(e, ['name', 'billing_email']) : null;
            showFormError(host, message, { field });
            return false;
          }
        }},
      ],
    });
  }

  _openInviteModal() {
    const a = this.state.agency;
    const body = `
      <div data-form-error></div>
      <form class="stack" data-form>
        <div class="field"><label>${esc(t('agency.invite_email'))}</label><input class="input" name="email" type="email" placeholder="kollega@iroda.hu" required /></div>
        <div class="field"><label>${esc(t('agency.invite_name'))}</label><input class="input" name="full_name" /></div>
        <div class="field"><label>${esc(t('agency.role'))}</label>
          <select class="input" name="role">
            <option value="MEMBER">MEMBER</option>
            <option value="ADMIN">ADMIN</option>
          </select>
        </div>
        <p class="faint">${esc(t('agency.invite_help'))}</p>
      </form>`;
    const m = showModal({
      title: t('agency.add_member'),
      body,
      actions: [
        { label: t('common.cancel') },
        { label: t('agency.invite_submit'), variant: 'primary', onClick: async () => {
          const host = m.el.querySelector('[data-modal-body]');
          const data = Object.fromEntries(new FormData(host.querySelector('[data-form]')).entries());
          try {
            const res = await api.post(`/agencies/${a.id}/members/invite`, data);
            if (res && res.pending === true && res.invitation && res.invitation.token) {
              const url = `${window.location.origin}/#/invite/${encodeURIComponent(res.invitation.token)}`;
              this.setState({ lastInvite: { url, email: res.email } });
            } else {
              toast.success(t('agency.member_added'));
            }
            this.setState({ loaded: false });
            this._load();
          }
          catch (e) {
            const message = e instanceof ApiError ? e.message : t('agency.errors.invite_member');
            const field = e instanceof ApiError ? fieldFromApiError(e, ['email', 'full_name', 'role']) : null;
            showFormError(host, message, { field });
            return false;
          }
        }},
      ],
    });
  }

  async _copyInviteUrl() {
    const i = this.state.lastInvite;
    if (!i) return;
    try {
      await navigator.clipboard.writeText(i.url);
    } catch {
      const inp = this.el.querySelector('[data-invite-url]');
      if (inp) { inp.select(); document.execCommand('copy'); }
    }
  }

  async _removeMember(memberId) {
    if (!confirm(t('agency.remove_confirm'))) return;
    try {
      await api.delete(`/agencies/${this.state.agency.id}/members/${memberId}`);
      toast.success(t('agency.member_removed'));
      this.setState({ loaded: false });
      this._load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('common.error'));
    }
  }
}
